import type {
  ScheduleRecommendation,
  ScheduleRecommendationEntry,
} from '@app/+onboarding/import/onboarding-import.models';

import {
  WEEKDAYS,
  type DraftScheduleSummary,
  type ReconciledEntry,
  type ReconciliationSource,
  type Weekday,
} from './onboarding-draft-schedule.models';

const WEEKDAY_ALIASES: Record<string, Weekday> = {
  mo: 'mo',
  mon: 'mo',
  monday: 'mo',
  tu: 'tu',
  tue: 'tu',
  tues: 'tu',
  tuesday: 'tu',
  we: 'we',
  wed: 'we',
  weds: 'we',
  wednesday: 'we',
  th: 'th',
  thu: 'th',
  thur: 'th',
  thurs: 'th',
  thursday: 'th',
  fr: 'fr',
  fri: 'fr',
  friday: 'fr',
  sa: 'sa',
  sat: 'sa',
  saturday: 'sa',
  su: 'su',
  sun: 'su',
  sunday: 'su',
};

/** Lowercase, strip diacritics and collapse whitespace so name-drift still matches. */
export function normalizeName(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

function parseWeekday(value: string): Weekday | null {
  return WEEKDAY_ALIASES[normalizeName(value).replace(/[^a-z]/g, '')] ?? null;
}

/**
 * Parse model/persisted day values (canonical codes or aliases like 'Monday')
 * into ordered weekday flags. Accepts `unknown` and guards the shape so one
 * malformed row — `days` absent or not an array — yields [] instead of throwing.
 */
export function toWeekdayFlags(days: unknown): Weekday[] {
  if (!Array.isArray(days)) {
    return [];
  }

  const flags = new Set<Weekday>();
  for (const day of days) {
    const flag = typeof day === 'string' ? parseWeekday(day) : null;
    if (flag) {
      flags.add(flag);
    }
  }
  return WEEKDAYS.filter((weekday) => flags.has(weekday));
}

/** Build a draft entry from a matched team / shift template / employee + weekdays. */
export function toReconciledEntry(
  team: { id: string; name: string },
  shift: ReconciliationSource['shifts'][number],
  employee: { id: string; name: string } | null,
  days: Weekday[],
): ReconciledEntry {
  return {
    teamId: team.id,
    shiftId: shift.id,
    // The shift template owns the department the roster is booked against.
    departmentId: shift.department_id,
    userId: employee?.id ?? '',
    startTime: shift.starttime,
    endTime: shift.endtime,
    days,
    teamName: team.name,
    // The grid and the recommendation both use the descriptive long_name.
    shiftName: shift.long_name || shift.name,
    employeeName: employee?.name ?? null,
  };
}

function matchEmployee(
  employeeName: string,
  employees: ReconciliationSource['employees'],
): { id: string; name: string } | null {
  const target = normalizeName(employeeName);
  if (!target) return null;

  // Only bind an exact full-name match when it is unambiguous — two people with
  // the same name is ordinary, and assigning to whichever sorts first would put
  // recurring shifts on the wrong person. Ambiguity → open shift.
  const exactMatches = employees.filter(
    (employee) =>
      !!employee.id &&
      (normalizeName(`${employee.first_name} ${employee.last_name}`) === target ||
        normalizeName(employee.name) === target),
  );
  const exact = exactMatches.length === 1 ? exactMatches[0] : null;
  if (exact?.id) return { id: exact.id, name: exact.name || `${exact.first_name} ${exact.last_name}`.trim() };

  // Fall back to a first-name match only when it is unambiguous.
  const firstNameMatches = employees.filter(
    (employee) => !!employee.id && normalizeName(employee.first_name) === target,
  );
  const match = firstNameMatches.length === 1 ? firstNameMatches[0] : null;
  if (match?.id) {
    return { id: match.id, name: match.name || `${match.first_name} ${match.last_name}`.trim() };
  }

  return null;
}

export interface RecommendationPartition {
  /** Entries whose team AND shift template exactly match, bound to those exact ids. */
  bound: ReconciledEntry[];
  /** Entries with no exact team/shift match — handed to the LLM 3rd pass for fuzzy resolution. */
  leftover: ScheduleRecommendationEntry[];
}

/**
 * Split the recommendation into deterministically-bound entries and leftovers.
 *
 * An entry whose `teamName` AND `shiftTemplateName` each EXACTLY match (via
 * normalizeName) a real team/shift template is bound RIGHT HERE to those exact
 * ids — the department and times come from the matched shift, and the employee
 * from the exact/unique-first-name match (open shift when it fails). This
 * guarantees the LLM can never substitute a different team/shift when an
 * exact-name match exists (e.g. "Work @ Home" must not become "Work @ Amsterdam").
 *
 * Every other entry (team or shift name has no exact match) is returned as a
 * leftover for the LLM to resolve by meaning.
 */
export function partitionRecommendation(
  recommendation: ScheduleRecommendation,
  source: ReconciliationSource,
): RecommendationPartition {
  const teamByName = indexByUniqueName(
    source.teams.filter((team) => !!team.id),
    (team) => normalizeName(team.name),
  );
  const shiftByName = buildShiftByName(source.shifts);

  const bound: ReconciledEntry[] = [];
  const leftover: ScheduleRecommendationEntry[] = [];

  for (const entry of recommendation) {
    // The recommendation is LLM-derived (and revived from sessionStorage), so
    // coerce the string fields rather than assume them — a malformed row must
    // fall through, never throw inside normalizeName.
    const teamName = typeof entry.teamName === 'string' ? entry.teamName : '';
    const shiftTemplateName = typeof entry.shiftTemplateName === 'string' ? entry.shiftTemplateName : '';
    const team = teamByName.get(normalizeName(teamName));
    const shift = shiftByName.get(normalizeName(shiftTemplateName));
    const teamId = team?.id;

    if (!team || !teamId || !shift) {
      // No exact team/shift match — let the LLM resolve it by meaning.
      leftover.push(entry);
      continue;
    }

    const days = toWeekdayFlags(entry.days);
    if (days.length === 0) {
      // Exact match but nothing schedulable — drop it rather than risk the LLM
      // re-binding an exact-name entry to a different template.
      continue;
    }

    const employee =
      typeof entry.employeeName === 'string' && entry.employeeName
        ? matchEmployee(entry.employeeName, source.employees)
        : null;

    bound.push(toReconciledEntry({ id: teamId, name: team.name }, shift, employee, days));
  }

  return { bound, leftover };
}

/**
 * Index a set of entities by a normalized name, leaving OUT any key that more
 * than one entity shares. An ambiguous name must fall through to the LLM 3rd
 * pass (which can disambiguate by meaning) rather than bind arbitrarily to
 * whichever entity happened to be indexed last. Empty keys are skipped.
 */
function indexByUniqueName<TEntity>(entities: TEntity[], keyOf: (entity: TEntity) => string): Map<string, TEntity> {
  const counts = new Map<string, number>();
  for (const entity of entities) {
    const key = keyOf(entity);
    if (key) {
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }

  const index = new Map<string, TEntity>();
  for (const entity of entities) {
    const key = keyOf(entity);
    if (key && counts.get(key) === 1) {
      index.set(key, entity);
    }
  }

  return index;
}

/**
 * Index shift templates by BOTH the descriptive `long_name` ("Work @ Home") and
 * the short `name` ("Wor"), normalized, so a recommendation can match either.
 *
 * A name is usable only when EXACTLY ONE template answers to it across both
 * namespaces. Short names collide easily (the created template's short `name` is
 * `long_name.slice(0, 3)`, so "Work @ Home" and "Work @ Amsterdam" both become
 * "Wor"); long names can collide too (the model may emit two templates with the
 * same descriptive name at different times); and a short name can clash with a
 * different template's long name. Counting owners across the whole set — rather
 * than de-duping each namespace separately — catches all three, so any ambiguous
 * name is left out and the entry falls through to the LLM pass instead of binding
 * to the wrong department/times.
 */
function buildShiftByName(shifts: ReconciliationSource['shifts']): Map<string, ReconciliationSource['shifts'][number]> {
  type Shift = ReconciliationSource['shifts'][number];

  const ownersByName = new Map<string, Set<Shift>>();

  const claim = (key: string, shift: Shift): void => {
    if (!key) {
      return;
    }

    const owners = ownersByName.get(key) ?? new Set<Shift>();
    owners.add(shift);
    ownersByName.set(key, owners);
  };

  for (const shift of shifts) {
    claim(normalizeName(shift.name), shift);
    claim(normalizeName(shift.long_name), shift);
  }

  const index = new Map<string, Shift>();

  for (const [key, owners] of ownersByName) {
    if (owners.size === 1) {
      index.set(key, [...owners][0]);
    }
  }

  return index;
}

/**
 * Deterministically match each recommendation entry against the teams, shift
 * templates and employees that actually exist, returning only the exact-name
 * bindings (leftovers are dropped). Used as the fast fallback when the LLM 3rd
 * pass is unavailable. No LLM call is made here, keeping the "aha moment" instant.
 */
export function reconcileRecommendation(
  recommendation: ScheduleRecommendation,
  source: ReconciliationSource,
): ReconciledEntry[] {
  return partitionRecommendation(recommendation, source).bound;
}

/**
 * Guarantee no employee is ever assigned two overlapping shifts on the same
 * weekday. Reconciliation (both the LLM pass and the deterministic fallback) can
 * emit e.g. "Work @ Home" on Mon–Wed and "Development" on Tue–Fri for the same
 * person, double-booking Tuesday.
 *
 * Deterministic and first-entry-wins: iterating in order, each employee keeps a
 * per-weekday list of claimed [start, end) ranges; a day that overlaps an
 * already-claimed range is dropped from the later entry. Entries left with no
 * days are removed. Open shifts (userId === '') are never touched. Overlap is
 * computed in minutes-since-midnight so shifts that cross midnight (e.g. a
 * 23:00–07:00 night shift, where end <= start) still collide correctly — a plain
 * string compare would treat such a range as empty and miss the double-booking.
 */
const MINUTES_PER_DAY = 24 * 60;

/** Parse a "HH:mm[:ss]" clock time into minutes since midnight. */
function toMinutesSinceMidnight(time: string): number {
  const [hours, minutes] = time.split(':');
  return Number(hours) * 60 + Number(minutes);
}

/**
 * Do two same-weekday clock ranges overlap? A range whose end is at or before its
 * start is read as crossing midnight (e.g. 23:00–07:00) and its end extended by a
 * day, so overnight shifts are compared on the same axis as day shifts. (The
 * post-midnight tail conceptually belongs to the next weekday's bucket; that
 * finer split isn't modelled here — this only prevents same-day double-booking.)
 */
function rangesOverlap(startA: string, endA: string, startB: string, endB: string): boolean {
  const startMinutesA = toMinutesSinceMidnight(startA);
  const startMinutesB = toMinutesSinceMidnight(startB);
  const endMinutesA = normalizeEndMinutes(startMinutesA, toMinutesSinceMidnight(endA));
  const endMinutesB = normalizeEndMinutes(startMinutesB, toMinutesSinceMidnight(endB));

  return startMinutesA < endMinutesB && startMinutesB < endMinutesA;
}

function normalizeEndMinutes(startMinutes: number, endMinutes: number): number {
  return endMinutes <= startMinutes ? endMinutes + MINUTES_PER_DAY : endMinutes;
}

export function dedupeEmployeeOverlaps(entries: ReconciledEntry[]): ReconciledEntry[] {
  const claimedByEmployee = new Map<string, Map<Weekday, { start: string; end: string }[]>>();
  const result: ReconciledEntry[] = [];

  for (const entry of entries) {
    if (!entry.userId) {
      result.push(entry);
      continue;
    }

    const claimedByDay = claimedByEmployee.get(entry.userId) ?? new Map<Weekday, { start: string; end: string }[]>();
    claimedByEmployee.set(entry.userId, claimedByDay);

    const keptDays: Weekday[] = [];
    for (const day of entry.days) {
      const ranges = claimedByDay.get(day) ?? [];
      const overlaps = ranges.some((range) => rangesOverlap(entry.startTime, entry.endTime, range.start, range.end));

      if (overlaps) {
        continue;
      }

      ranges.push({ start: entry.startTime, end: entry.endTime });
      claimedByDay.set(day, ranges);
      keptDays.push(day);
    }

    if (keptDays.length > 0) {
      result.push({ ...entry, days: keptDays });
    }
  }

  return result;
}

export function summarizeDraft(entries: ReconciledEntry[], occurrenceCount: number): DraftScheduleSummary {
  return {
    entryCount: entries.length,
    occurrenceCount,
    assignedCount: entries.filter((entry) => !!entry.userId).length,
    openCount: entries.filter((entry) => !entry.userId).length,
  };
}
